import {
    InputLabel, SelectChangeEvent, Typography,
} from '@mui/material';
import {
    BusinessOutlined as BusinessOutlinedIcon,
    SearchOffOutlined as SearchOffOutlinedIcon,
} from '@mui/icons-material';
import {
    useMemo, useState,
} from 'react';
import { CompanyViewModel } from '../../../mappers/companyMapper';
import { LoggedInUser, UserProfileViewModel } from '../../../types/auth.types';
import { JobSearchResultViewModel } from '../../../types/jobs.types';
import JobItemList from '../containers/JobItemList/JobItemList.container';
import Pagination from '../components/Pagination/Pagination';
import Select from '../foundation/Select';
import Page from '../layout/base.page';
import {
    ClearFiltersButton,
    CompanyCover,
    CompanyDescription,
    CompanyHeader,
    CompanyLogoFallback,
    CompanyLogoFrame,
    CompanyLogoImage,
    CompanyName,
    CompanyPageContainer,
    CompanySummary,
    EmptyState,
    FilterControl,
    FiltersPanel,
    JobsHeader,
    JobsSection,
    JobsTitle,
    PaginationWrapper,
    ResultsCount,
} from './CompanyProfile.page.styles';

const ALL_FILTER_VALUE = 'all';
const JOBS_PER_PAGE = 6;

export type CompanyProfileViewModel = CompanyViewModel & {
    coverImageUrl?: string;
};

export type CompanyProfilePageProps = {
    company: CompanyProfileViewModel;
    jobs: JobSearchResultViewModel[];
    url: string;
    user?: LoggedInUser;
    userProfile?: UserProfileViewModel;
};

const getCompanyLogoUrl = (company: CompanyProfileViewModel): string | undefined => {
    if (!company.logo) return undefined;

    const companyPath = company.companyid.toString().split('').join('/');
    return `/assets/logos/${companyPath}/${company.logo}`;
};

const getResultsText = (count: number): string => {
    if (count === 0) return 'לא נמצאו משרות';
    if (count === 1) return 'נמצאה משרה אחת';
    return `נמצאו ${count} משרות`;
};

// JobItem keeps its desktop action column beside expanded details when this callback is provided.
const keepExpandedJobLayout = () => undefined;

function CompanyProfilePage({
    company,
    jobs: initialJobs,
    url,
    user: initialUser,
    userProfile: initialUserProfile,
}: CompanyProfilePageProps) {
    const [jobs, setJobs] = useState(initialJobs);
    const [user, setUser] = useState(initialUser);
    const [userProfile, setUserProfile] = useState(initialUserProfile);
    const [selectedCategory, setSelectedCategory] = useState(ALL_FILTER_VALUE);
    const [selectedLocation, setSelectedLocation] = useState(ALL_FILTER_VALUE);
    const [currentPage, setCurrentPage] = useState(1);

    const categoryOptions = useMemo(() => {
        const categories = new Set<string>();
        jobs.forEach(job => {
            job.tags?.filter(tag => !tag.vertical).forEach(tag => categories.add(tag.tag));
        });

        return [
            { text: 'כל התחומים והתפקידים', value: ALL_FILTER_VALUE },
            ...Array.from(categories)
                .sort((first, second) => first.localeCompare(second, 'he'))
                .map(category => ({ text: category, value: category })),
        ];
    }, [jobs]);

    const locationOptions = useMemo(() => {
        const locations = new Set(
            jobs.map(job => job.formatted_address).filter((location): location is string => !!location),
        );

        return [
            { text: 'כל האזורים', value: ALL_FILTER_VALUE },
            ...Array.from(locations)
                .sort((first, second) => first.localeCompare(second, 'he'))
                .map(location => ({ text: location, value: location })),
        ];
    }, [jobs]);

    const visibleJobs = useMemo(() => jobs.filter(job => {
        const matchesCategory = selectedCategory === ALL_FILTER_VALUE
            || job.tags?.some(tag => tag.tag === selectedCategory);
        const matchesLocation = selectedLocation === ALL_FILTER_VALUE
            || job.formatted_address === selectedLocation;

        return matchesCategory && matchesLocation;
    }), [jobs, selectedCategory, selectedLocation]);

    const totalPages = Math.ceil(visibleJobs.length / JOBS_PER_PAGE);
    const activePage = Math.min(currentPage, Math.max(totalPages, 1));
    const paginatedJobs = visibleJobs.slice(
        (activePage - 1) * JOBS_PER_PAGE,
        activePage * JOBS_PER_PAGE,
    );

    const handleCategoryChange = (event: SelectChangeEvent<unknown>) => {
        setSelectedCategory(String(event.target.value));
        setCurrentPage(1);
    };

    const handleLocationChange = (event: SelectChangeEvent<unknown>) => {
        setSelectedLocation(String(event.target.value));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSelectedCategory(ALL_FILTER_VALUE);
        setSelectedLocation(ALL_FILTER_VALUE);
        setCurrentPage(1);
    };

    const updateJobs = (updatedJobs: JobSearchResultViewModel[]) => {
        const updatedJobsById = new Map(updatedJobs.map(job => [job.jobid, job]));
        setJobs(currentJobs => currentJobs.map(job => updatedJobsById.get(job.jobid) ?? job));
    };

    const logoUrl = getCompanyLogoUrl(company);
    const filtersAreActive = selectedCategory !== ALL_FILTER_VALUE
        || selectedLocation !== ALL_FILTER_VALUE;

    return (
        <Page
            bg="main"
            breadCrumbs={[
                { text: 'חברות', url: '/' },
                { text: company.name, url },
            ]}
            pageHeaderTitle={company.name}
            showPageHeader={false}
            url={url}
            user={user}
        >
            <CompanyPageContainer maxWidth="lg">
                <CompanyHeader>
                    <CompanyCover $imageUrl={company.coverImageUrl} />
                    <CompanyLogoFrame>
                        {logoUrl ? (
                            <CompanyLogoImage alt={`לוגו ${company.name}`} src={logoUrl} />
                        ) : (
                            <CompanyLogoFallback aria-label={`לוגו ${company.name}`} role="img">
                                <BusinessOutlinedIcon />
                            </CompanyLogoFallback>
                        )}
                    </CompanyLogoFrame>
                    <CompanySummary>
                        <CompanyName component="h1">{company.name}</CompanyName>
                        <CompanyDescription component="p">
                            {company.description || 'פרטים נוספים על החברה יעודכנו בקרוב.'}
                        </CompanyDescription>
                    </CompanySummary>
                </CompanyHeader>

                <JobsSection>
                    <JobsHeader>
                        <JobsTitle component="h2">המשרות שלנו</JobsTitle>
                        <ResultsCount>{getResultsText(visibleJobs.length)}</ResultsCount>
                    </JobsHeader>

                    <FiltersPanel aria-label="סינון משרות">
                        <FilterControl fullWidth>
                            <InputLabel id="company-jobs-category-label">תחום או תפקיד</InputLabel>
                            <Select
                                fullWidth
                                label="תחום או תפקיד"
                                labelId="company-jobs-category-label"
                                onChange={handleCategoryChange}
                                options={categoryOptions}
                                value={selectedCategory}
                            />
                        </FilterControl>
                        <FilterControl fullWidth>
                            <InputLabel id="company-jobs-location-label">אזור</InputLabel>
                            <Select
                                fullWidth
                                label="אזור"
                                labelId="company-jobs-location-label"
                                onChange={handleLocationChange}
                                options={locationOptions}
                                value={selectedLocation}
                            />
                        </FilterControl>
                        <ClearFiltersButton
                            color="secondary"
                            disabled={!filtersAreActive}
                            onClick={clearFilters}
                            variant="outlined"
                        >
                            ניקוי סינון
                        </ClearFiltersButton>
                    </FiltersPanel>

                    {visibleJobs.length > 0 ? (
                        <JobItemList
                            jobs={paginatedJobs}
                            onJobsChange={updateJobs}
                            onReadMoreClick={keepExpandedJobLayout}
                            onUserChange={setUser}
                            onUserProfileChange={setUserProfile}
                            showDetailsLink={false}
                            showRemoveJob
                            showTags={false}
                            user={user}
                            userProfile={userProfile}
                        />
                    ) : (
                        <EmptyState>
                            <SearchOffOutlinedIcon />
                            <Typography component="h3" fontWeight={700} variant="h6">
                                לא נמצאו משרות שמתאימות לסינון שבחרת
                            </Typography>
                            <Typography color="text.secondary">
                                אפשר לנקות את הסינון ולראות את כל המשרות של החברה.
                            </Typography>
                        </EmptyState>
                    )}

                    {totalPages > 1 && (
                        <PaginationWrapper aria-label="עימוד משרות">
                            <Pagination
                                currentPage={activePage}
                                gotoPage={setCurrentPage}
                                pages={totalPages}
                            />
                        </PaginationWrapper>
                    )}
                </JobsSection>
            </CompanyPageContainer>
        </Page>
    );
}

export default CompanyProfilePage;
